import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Check,
  Copy,
  Download,
  ExternalLink,
  Eye,
  FileText,
  MoreHorizontal,
  Pencil,
  Send,
  Share2,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  useActiveOrganization,
  useActiveOrganizationId,
} from '@repo/api-client/react';
import {
  useClient,
  useDeleteInvoice,
  useDuplicateInvoice,
  useInvoice,
  useInvoiceActivities,
  useInvoicePayments,
  useTransitionInvoice,
} from '@repo/queries';
import type { Invoice, InvoiceActivity } from '@repo/api-client/dtos';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { displayWireMoney } from '@/features/money';
import { describeAppError } from '@/features/common/describeAppError';
import { useToast } from '@/components/ui/use-toast';
import { SendInvoiceDialog } from '@/features/invoice/SendInvoiceDialog';
import { RemindInvoiceDialog } from '@/features/invoice/RemindInvoiceDialog';
import { RecordPaymentDialog } from '@/features/invoice/RecordPaymentDialog';
import { ClientAvatar, inferClientTag } from '@/features/avatar';
import { useActiveRole } from '@/features/auth/useActiveRole';
import { useInvoicePdf } from '@/features/invoice/useInvoicePdf';

export function InvoiceDetailScreen() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const orgId = useActiveOrganizationId();
  const org = useActiveOrganization();
  const { invoice, loading, error } = useInvoice(invoiceId ?? '');
  const { client } = useClient(invoice?.clientId ?? '', { enabled: !!invoice });
  const { activities } = useInvoiceActivities(invoiceId ?? '', { enabled: !!invoice });
  const { payments } = useInvoicePayments(invoiceId ?? '');
  const { transitionInvoice, loading: transitioning } = useTransitionInvoice();
  const { duplicateInvoice, loading: duplicating } = useDuplicateInvoice();
  const { deleteInvoice, loading: deleting } = useDeleteInvoice();
  const { toast } = useToast();
  const { canEditOrg } = useActiveRole();
  const pdf = useInvoicePdf(
    orgId ?? '',
    invoiceId ?? '',
    invoice ? `${invoice.invoiceNumber}.pdf` : 'invoice.pdf',
  );

  const [sendOpen, setSendOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  if (loading) {
    return (
      <Page>
        <Skeleton className="h-8 w-48" />
        <div className="mt-6 grid gap-4">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
      </Page>
    );
  }

  if (error || !invoice) {
    return (
      <Page>
        <Alert variant="danger">
          {error ? describeAppError(error).message : 'Invoice not found.'}
        </Alert>
      </Page>
    );
  }

  const recipientEmail =
    typeof client?.contact?.['email'] === 'string'
      ? (client.contact['email'] as string)
      : undefined;

  const canEdit = canEditOrg && invoice.status === 'draft';
  const canSend = canEditOrg && invoice.status === 'draft';
  const canRemind =
    canEditOrg &&
    (invoice.status === 'sent' ||
      invoice.status === 'overdue' ||
      invoice.status === 'pending');
  const canMarkPaid =
    canEditOrg && invoice.status !== 'paid' && invoice.status !== 'cancelled';
  const canCancel =
    canEditOrg &&
    (invoice.status === 'sent' ||
      invoice.status === 'pending' ||
      invoice.status === 'overdue');
  const publicShareUrl =
    invoice.publicShareToken && typeof window !== 'undefined'
      ? `${window.location.origin}/i/${invoice.publicShareToken}`
      : null;

  async function handleTransition(to: 'paid' | 'cancelled') {
    if (!invoice) return;
    try {
      await transitionInvoice({ invoiceId: invoice.id, to });
      toast({
        title: to === 'paid' ? 'Marked as paid' : 'Invoice cancelled',
        variant: 'success',
      });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  async function handleDuplicate() {
    if (!orgId || !invoice) return;
    try {
      const next = await duplicateInvoice({
        organizationId: orgId,
        invoiceId: invoice.id,
      });
      toast({
        title: 'Duplicated',
        description: `${next.invoiceNumber} created from ${invoice.invoiceNumber}.`,
        variant: 'success',
      });
      navigate(`/invoices/${next.id}`);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  async function handleDelete() {
    if (!invoice) return;
    if (typeof window !== 'undefined' && !window.confirm(`Delete ${invoice.invoiceNumber}? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteInvoice(invoice.id);
      toast({ title: 'Invoice deleted', variant: 'success' });
      navigate('/invoices');
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Page>
      <Button variant="link" size="sm" asChild className="-ml-2 mb-4">
        <Link to="/invoices">
          <ArrowLeft className="size-4" /> Back to invoices
        </Link>
      </Button>

      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            {invoice.invoiceNumber}
            <StatusBadge status={invoice.status} />
          </span>
        }
        description={
          <span className="inline-flex items-center gap-2.5">
            <ClientAvatar
              name={client?.name ?? invoice.clientNameSnapshot ?? '?'}
              photoUrl={client?.photoUrl ?? null}
              tag={inferClientTag({
                name: client?.name ?? invoice.clientNameSnapshot ?? '',
                company: client?.company,
              })}
              size={28}
            />
            {client ? (
              <Link
                to={`/clients/${client.id}`}
                className="text-sm font-medium text-foreground hover:underline"
              >
                {client.displayName || client.name}
              </Link>
            ) : (
              <span className="text-sm font-medium text-foreground">
                {invoice.clientNameSnapshot}
              </span>
            )}
          </span>
        }
        actions={
          <>
            {canEdit ? (
              <Button variant="outline" asChild>
                <Link to={`/invoices/${invoice.id}/edit`}>
                  <Pencil className="size-4" /> Edit
                </Link>
              </Button>
            ) : null}
            <Button variant="outline" asChild>
              <Link to={`/invoices/${invoice.id}/preview`}>
                <Eye className="size-4" /> Preview
              </Link>
            </Button>
            <Button
              variant="outline"
              loading={pdf.downloading}
              onClick={async () => {
                try {
                  await pdf.downloadPdf();
                  toast({ title: 'PDF downloaded', variant: 'success' });
                } catch (err) {
                  const desc = describeAppError(err);
                  toast({ title: desc.title, description: desc.message, variant: 'danger' });
                }
              }}
            >
              <Download className="size-4" /> Download PDF
            </Button>
            <Button
              variant="outline"
              loading={pdf.sharing}
              onClick={async () => {
                try {
                  await pdf.sharePdf();
                } catch (err) {
                  const desc = describeAppError(err);
                  toast({ title: desc.title, description: desc.message, variant: 'danger' });
                }
              }}
            >
              <Share2 className="size-4" /> Share
            </Button>
            {canSend ? (
              <Button onClick={() => setSendOpen(true)}>
                <Send className="size-4" /> Send
              </Button>
            ) : null}
            {canRemind ? (
              <Button onClick={() => setRemindOpen(true)}>
                <Bell className="size-4" /> Remind
              </Button>
            ) : null}
            {canMarkPaid ? (
              <Button variant="outline" onClick={() => setPaymentOpen(true)}>
                <Check className="size-4" /> Record payment
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="More actions">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {canEditOrg ? (
                  <DropdownMenuItem onSelect={handleDuplicate} disabled={duplicating}>
                    <Copy className="size-4" /> Duplicate
                  </DropdownMenuItem>
                ) : null}
                {canMarkPaid ? (
                  <DropdownMenuItem
                    onSelect={() => handleTransition('paid')}
                    disabled={transitioning}
                  >
                    <Check className="size-4" /> Mark as paid
                  </DropdownMenuItem>
                ) : null}
                {canCancel ? (
                  <DropdownMenuItem
                    onSelect={() => handleTransition('cancelled')}
                    disabled={transitioning}
                  >
                    <XCircle className="size-4" /> Cancel invoice
                  </DropdownMenuItem>
                ) : null}
                {publicShareUrl ? (
                  <DropdownMenuItem asChild>
                    <a href={publicShareUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="size-4" /> Open public link
                    </a>
                  </DropdownMenuItem>
                ) : null}
                {canEditOrg ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={handleDelete}
                      disabled={deleting}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="size-4" /> Delete
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Line items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit price</TableHead>
                    <TableHead className="text-right">Line total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(invoice.lineItems ?? []).map((line) => {
                    const qty = Number(line.quantity);
                    const unit = BigInt(line.unitPrice);
                    const lineTotalMinor =
                      Number.isFinite(qty) && qty > 0
                        ? ((unit * BigInt(Math.round(qty * 1000))) / 1000n).toString()
                        : '0';
                    return (
                      <TableRow key={line.id}>
                        <TableCell>
                          <div className="font-medium text-foreground">{line.description}</div>
                          {line.notes ? (
                            <p className="mt-0.5 text-xs text-muted-foreground">{line.notes}</p>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {line.quantity}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {displayWireMoney(line.unitPrice, invoice.currency)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm font-medium">
                          {displayWireMoney(lineTotalMinor, invoice.currency)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {(activities ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nothing here yet. Sending, payments and reminders will appear here.
                </p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {(activities ?? []).map((act) => (
                    <ActivityRow key={act.id} activity={act} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Totals</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <RowKV label="Subtotal" value={displayWireMoney(invoice.totals.subtotal, invoice.currency)} />
              <RowKV
                label="Discount"
                value={`− ${displayWireMoney(invoice.totals.discountTotal, invoice.currency)}`}
              />
              <RowKV
                label="Tax"
                value={displayWireMoney(invoice.totals.taxTotal, invoice.currency)}
              />
              <Separator className="my-2" />
              <RowKV
                label="Total"
                value={displayWireMoney(invoice.totals.total, invoice.currency)}
                strong
              />
              <RowKV
                label="Paid"
                value={displayWireMoney(invoice.totals.amountPaid, invoice.currency)}
              />
              <RowKV
                label="Amount due"
                value={displayWireMoney(invoice.totals.amountDue, invoice.currency)}
                strong
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <RowKV label="Currency" value={invoice.currency} />
              <RowKV label="Issued" value={formatDate(invoice.issueDate)} />
              <RowKV label="Due" value={formatDate(invoice.dueDate)} />
              {invoice.sentAt ? (
                <RowKV label="Sent" value={formatDate(invoice.sentAt)} />
              ) : null}
              {invoice.paidAt ? (
                <RowKV label="Paid" value={formatDate(invoice.paidAt)} />
              ) : null}
              {invoice.viewedAt ? (
                <RowKV label="Viewed" value={formatDate(invoice.viewedAt)} />
              ) : null}
              <RowKV label="Template" value={invoice.templateId ?? 'classic'} />
            </CardContent>
          </Card>

          {publicShareUrl ? (
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-base">Public link</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="break-all rounded-md bg-secondary/50 p-2 font-mono text-xs">
                  {publicShareUrl}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    if (typeof navigator !== 'undefined' && navigator.clipboard) {
                      void navigator.clipboard.writeText(publicShareUrl);
                      toast({ title: 'Link copied', variant: 'success' });
                    }
                  }}
                >
                  <Copy className="size-4" /> Copy link
                </Button>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Payments</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {payments && payments.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {formatDate(p.paymentDate)} · {p.method.replace('_', ' ')}
                      </span>
                      <span className="font-mono text-foreground">
                        {displayWireMoney(p.amount, p.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
              )}
              {canMarkPaid ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setPaymentOpen(true)}
                >
                  <Check className="size-4" /> Record payment
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <SendInvoiceDialog
        invoice={invoice}
        defaultRecipient={recipientEmail}
        open={sendOpen}
        onOpenChange={setSendOpen}
        onSent={() => {
          const q = new URLSearchParams({
            client: invoice.clientNameSnapshot,
            amount: invoice.totals.amountDue,
            currency: invoice.currency,
            invoiceId: invoice.id,
          });
          navigate(`/invoices/sent?${q.toString()}`);
        }}
      />
      <RemindInvoiceDialog
        invoice={invoice}
        defaultRecipient={recipientEmail}
        open={remindOpen}
        onOpenChange={setRemindOpen}
      />
      <RecordPaymentDialog
        invoice={invoice}
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
      />

      {!org ? <span className="sr-only">No active org</span> : null}
    </Page>
  );
}

function RowKV({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono ${strong ? 'font-semibold text-foreground' : ''}`}>{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: Invoice['status'] }) {
  const variant: 'success' | 'warning' | 'danger' | 'default' | 'accent' =
    status === 'paid'
      ? 'success'
      : status === 'overdue'
        ? 'danger'
        : status === 'sent' || status === 'pending'
          ? 'warning'
          : 'default';
  return (
    <Badge variant={variant} className="capitalize">
      {status}
    </Badge>
  );
}

function ActivityRow({ activity }: { activity: InvoiceActivity }) {
  return (
    <li className="flex items-start gap-3">
      <div className="mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
        <ActivityIcon type={activity.type} />
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="text-sm text-foreground">{activity.description}</p>
        <p className="text-xs text-muted-foreground">{formatDate(activity.createdAt)}</p>
      </div>
    </li>
  );
}

function ActivityIcon({ type }: { type: string }) {
  if (type.includes('paid')) return <Check className="size-3.5" />;
  if (type.includes('sent') || type.includes('email')) return <Send className="size-3.5" />;
  if (type.includes('reminder')) return <Bell className="size-3.5" />;
  if (type.includes('viewed')) return <Eye className="size-3.5" />;
  if (type.includes('cancel')) return <XCircle className="size-3.5" />;
  return <FileText className="size-3.5" />;
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  } catch {
    return value;
  }
}
